// Per-engine CTE self-visibility matrix + reference-based self-reference
// detection (Plan 10 / spec §2.8 S14). This is the SINGLE dialect-truth home
// for "may a CTE reference itself inside its own body on this engine?".
//
// SCOPE BOUNDARY — read before touching this file:
// Resolver scope (`resolveScope.ts`) is UNCONDITIONALLY self-visible: a CTE
// body always resolves its own name (earlier siblings + self), engine-blind and
// keyword-blind. That behaviour landed in `1222ba3e` and is pinned "never flips"
// (`resolveScope.test.ts:178-209`, incl. `:202` pg-without-RECURSIVE). This
// matrix MUST NOT be wired into scope — it drives ONLY:
//   • the rename planner's in-body self-reference scan/rewrite gate (Plan 10 T3)
//   • the recursion badge / anchor-recursive labels (Plan 10 T5)
// so a keyword-less pg/mysql "recursive" CTE (which those engines reject) is not
// badged recursive and its non-existent self-reference is not rewritten, while
// scope keeps its intentional over-suggest posture.

import type { ParseOptions, SqlEngine } from '../../sql-grammar/model';
import { findCteSelfReferences } from '../edit/applyStructuralEdit.cte';

/**
 * Is a CTE self-reference legal inside its own body on `engine`, given whether
 * the WITH clause carries the RECURSIVE keyword? Drives the rename scan (T3) and
 * recursion detection (T5) — NOT resolver scope (see the file header).
 *
 * - mssql / oracle: recursion is keyword-less → always legal (Oracle's recursive
 *   form additionally requires an explicit column list, enforced elsewhere).
 * - postgres / mysql: legal ONLY when the clause is `WITH RECURSIVE` (MySQL 8.0+).
 * - sqlite: always legal — the RECURSIVE keyword has no semantic effect
 *   (https://sqlite.org/lang_with.html). Verified at execution by Plan 10 T6's
 *   decision-3 probe (`vqe-recursive-cte.realDb.spec.ts`, 2026-07-08: the
 *   keyword-less form returned the full 5-row recursion) — row flipped per the
 *   pre-authorized H-3 resolution; scope never changes.
 */
export function cteSelfVisibility(engine: SqlEngine, clauseIsRecursive: boolean): boolean {
  switch (engine) {
    case 'mssql':
    case 'oracle':
    case 'sqlite':
      return true;
    case 'postgres':
    case 'mysql':
      return clauseIsRecursive;
  }
}

/**
 * Reference-based per-binding recursion detection: does the CTE body at
 * `[bodyStart, bodyEnd)` contain a self-reference in a relation slot? Wraps the
 * body-bounded, relation-slot-gated scanner `findCteSelfReferences` (the single
 * lexical implementation lives in the edit module) — a binding is recursion-
 * detectable iff this returns true AND the engine's `cteSelfVisibility` holds
 * (the caller AND-combines the two per decision 4). Purely lexical + slot: the
 * scope-aware shadow resolution that the rename planner adds is not applied here.
 */
export function detectCteSelfReference(args: {
  readonly source: string;
  readonly options: ParseOptions;
  readonly bodyStart: number;      // just inside the body's open paren
  readonly bodyEnd: number;        // just before the close paren
  readonly cteName: string;
  readonly cteNameQuoted: boolean;
}): boolean {
  return (
    findCteSelfReferences({
      source: args.source,
      options: args.options,
      // `statementEndOffset` / `definitionEnd` are part of the shared
      // FindCteReferencesArgs shape but unused by the body-window scan — the
      // scan window is `[bodyStart, bodyEnd)`. Pass the body bounds so no stale
      // downstream offset leaks in.
      statementEndOffset: args.bodyEnd,
      definitionEnd: args.bodyStart,
      cteName: args.cteName,
      cteNameQuoted: args.cteNameQuoted,
      bodyStart: args.bodyStart,
      bodyEnd: args.bodyEnd,
    }).length > 0
  );
}
